"use client";

import { StateCreator } from "zustand";
import {
  carePlanTemplates,
  IActionReminder,
  ICarePlanAction,
  ICarePlanTemplate,
  ICategorizedItemsList,
} from "../components/testData/carePlanData";
import { assessmentsList, IAssessmentOption } from "../constants/careplan";
import {
  Action,
  ActionStatusEnum,
  Broadcast,
  BroadcastCategoryEnum,
  BulkDeleteItemsRequest,
  Event,
  ReminderReminderTypeEnum,
} from "@clients/velys-infrastructure/api";
import { isMomentGeneratedId } from "../helpers";
import { v4 as uuidv4 } from "uuid";

export const initialNewReminder = {
  message: "",
  execution_date: "",
  always_send: false,
  days_before_due_to_remind: 0,
  reminder_type: ReminderReminderTypeEnum.Email,
  reminder_repeat_interval_days: 0,
};

export const initialCategorizedActions = {
  exercises: { preop: [], postop: [] },
  education: { preop: [], postop: [] },
  actions: { preop: [], postop: [] },
  events: { preop: [], postop: [] },
};

export const initialPatient = {
  mrn: "",
  firstName: "",
  lastName: "",
  dob: "",
  gender: "",
  email: "",
  mobile: "",
  carePlan: "",
  procedure: "",
  surgeryDate: "",
  surgeon: "",
  location: "",
};

export interface IConvertedToCarePlan {
  actions: Action[];
  broadcasts: Broadcast[];
  events: Event[];
}

export type IPatientField = {
  mrn: string;
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  email: string;
  mobile: string;
  carePlan: string;
  procedure: string;
  surgeryDate: string;
  surgeon: string;
  location: string;
};

export type CarePlanState = {
  carePlans: ICarePlanTemplate[];
  currentCarePlanId: string | null;
  currentCarePlan: ICarePlanTemplate | null;
  selectedAction: ICarePlanAction | null;
  isAIOpen: boolean;
  isCreateAction: boolean;
  assesmentTypes: IAssessmentOption[];
  sortedCarePlanActions: ICategorizedItemsList;
  selectedType: string;
  selectedTimeframe: string;
  enrollmentStep: string;
  newPatient: IPatientField;
  pendingEnrollmentId: string;
  isPredefinedMenuOpen: boolean;
  filterType: string;
  sendReminder: boolean;
  deletedActions: string[];
  deleteCarePlanItems: BulkDeleteItemsRequest;

  // actions
  setCurrentCarePlanId: (planId: string | null) => void;
  setCurrentCarePlan: (plan: ICarePlanTemplate | null) => void;
  setSelectedAction: (action: ICarePlanAction | null) => void;
  setIsAIOpen: (isOpen: boolean) => void;
  setIsCreateAction: (isCreate: boolean) => void;
  setSortedCarePlanActions: (sorted: ICategorizedItemsList) => void;
  setSelectedType: (type: string) => void;
  setSelectedTimeframe: (timeframe: string) => void;
  setEnrollmentStep: (step: string) => void;
  setNewPatient: (patient: IPatientField) => void;
  setPendingEnrollmentId: (pendingEnrollmentId: string) => void;
  setIsPredefinedMenuOpen: (isOpen: boolean) => void;
  setFilterType: (type: string) => void;
  setSendReminder: (sendReminder: boolean) => void;
  setDeletedActions: (actions: string[]) => void;

  refactorCarePlanTemplateActions: () => void;
  convertToCarePlanActions: (
    items?: ICategorizedItemsList,
    removeActions?: string[],
    patientEmail?: string
  ) => IConvertedToCarePlan;
  updateDeleteCarePlanItems: (deleteItems: BulkDeleteItemsRequest) => void;
  editEnrollmentItemsPayload: () => IConvertedToCarePlan;
  deleteEnrollmentItemsPayload: () => BulkDeleteItemsRequest;
};

export const createCarePlanSlice: StateCreator<CarePlanState> = (set, get) => ({
  carePlans: carePlanTemplates,
  currentCarePlanId: null,
  currentCarePlan: null,
  selectedAction: null,
  isAIOpen: false,
  isCreateAction: false,
  assesmentTypes: assessmentsList,
  sortedCarePlanActions: initialCategorizedActions,
  selectedType: "Action Items",
  selectedTimeframe: "Pre-Op",
  enrollmentStep: "patient",
  newPatient: initialPatient,
  pendingEnrollmentId: "",
  isPredefinedMenuOpen: false,
  filterType: "actions",
  sendReminder: false,
  deletedActions: [],
  deleteCarePlanItems: {
    actions: [],
    events: [],
    broadcasts: [],
  },

  // actions
  setCurrentCarePlanId: (planId) => set({ currentCarePlanId: planId }),
  setCurrentCarePlan: (plan) => {
    set({ currentCarePlan: plan });
    get().refactorCarePlanTemplateActions();
  },
  setSelectedAction: (action) => set({ selectedAction: action }),
  setIsAIOpen: (isOpen) => set({ isAIOpen: isOpen }),
  setIsCreateAction: (isCreate) => set({ isCreateAction: isCreate }),
  setSortedCarePlanActions: (sortedCarePlanActions) => {
    set({ sortedCarePlanActions });
  },
  setSelectedType: (type) => set({ selectedType: type }),
  setSelectedTimeframe: (timeframe) => set({ selectedTimeframe: timeframe }),
  setEnrollmentStep: (step) => set({ enrollmentStep: step }),
  setNewPatient: (patient) => set({ newPatient: patient }),
  setPendingEnrollmentId: (pendingEnrollmentId) => set({ pendingEnrollmentId }),
  setIsPredefinedMenuOpen: (isOpen) => set({ isPredefinedMenuOpen: isOpen }),
  setFilterType: (type) => set({ filterType: type }),
  setSendReminder: (sendReminder) => set({ sendReminder }),
  setDeletedActions: (actions) => set({ deletedActions: actions }),

  refactorCarePlanTemplateActions: () => {
    const currentCarePlan = get().currentCarePlan;
    if (!currentCarePlan) {
      set({ sortedCarePlanActions: initialCategorizedActions });
      return initialCategorizedActions;
    }

    const categorizeByDays = (items: ICarePlanAction[], type: string) => {
      if (!items) return { preop: [], postop: [] };

      // create uuid for items w/o ID (for generic careplan tempalte)
      const itemsWithType = items.map((item) => ({
        ...item,
        id: item.id || crypto.randomUUID() || uuidv4(),
        type: type,
      }));

      const preop = itemsWithType
        .filter(
          (item) => parseInt(item.delta_days_from_surgery ?? "0", 10) <= 0
        )
        .sort(
          (a, b) =>
            parseInt(a.delta_days_from_surgery ?? "0", 10) -
            parseInt(b.delta_days_from_surgery ?? "0", 10)
        );

      const postop = itemsWithType
        .filter((item) => parseInt(item.delta_days_from_surgery ?? "0", 10) > 0)
        .sort(
          (a, b) =>
            parseInt(a.delta_days_from_surgery ?? "0", 10) -
            parseInt(b.delta_days_from_surgery ?? "0", 10)
        );

      return { preop, postop };
    };

    const categorized: ICategorizedItemsList = {
      exercises: categorizeByDays(
        currentCarePlan.broadcasts.filter(
          (item) => item.category?.toLowerCase() === "exercise"
        ),
        "broadcast"
      ),
      education: categorizeByDays(
        currentCarePlan.broadcasts.filter(
          (item) => item.category?.toLowerCase() === "education"
        ),
        "broadcast"
      ),
      actions: categorizeByDays(currentCarePlan.actions, "action-item"),
      events: categorizeByDays(currentCarePlan.events, "event"),
    };

    set({ sortedCarePlanActions: categorized });
    return categorized;
  },

  convertToCarePlanActions: (
    categorizedItems?: ICategorizedItemsList,
    removeActions?: string[],
    patientEmail?: string
  ) => {
    const itemsToConvert = categorizedItems || get().sortedCarePlanActions;
    const deleteActions = removeActions || get().deletedActions;
    if (!itemsToConvert) {
      return { actions: [], broadcasts: [], events: [] };
    }

    const processReminders = (reminders?: IActionReminder[]) => {
      if (
        !reminders ||
        (reminders.length === 1 && reminders[0].message === "")
      ) {
        return [];
      }
      // make sure reminder destination is set
      return reminders.map((reminder) => ({
        ...reminder,
        destination: patientEmail,
        reminder_type: ReminderReminderTypeEnum.Email,
        reminder_repeat_interval_days: 0,
        always_send: false,
      }));
    };

    const actions = [
      ...itemsToConvert.actions.preop,
      ...itemsToConvert.actions.postop,
    ]
      .filter((action) => {
        return !deleteActions.includes(action.id!);
      })
      .map((action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = action;

        return {
          ...rest,
          required: action.required ?? true,
          status: (action.status || "pending") as ActionStatusEnum,
          reminders: processReminders(action.reminders),
          source: action.source || "custom",
        };
      });

    const broadcasts = [
      ...itemsToConvert.exercises.preop,
      ...itemsToConvert.exercises.postop,
      ...itemsToConvert.education.preop,
      ...itemsToConvert.education.postop,
    ]
      .filter((action) => {
        return !deleteActions.includes(action.id!);
      })
      .map((action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = action;
        const validAssets = Array.isArray(action.assets)
          ? action.assets.filter((a) => !!a?.id)
          : [];
        const legacyAssetValid = !!action.asset?.id;
        const broadcast = {
          ...rest,
          category: (action.category || "Exercise") as BroadcastCategoryEnum,
          asset: legacyAssetValid
            ? {
                ...action.asset,
                name: action.asset?.name || action.title,
              }
            : null,
          assets: validAssets,
          reminders: processReminders(action.reminders),
        };
        return broadcast as unknown as Broadcast;
      });

    const events = [
      ...itemsToConvert.events.preop,
      ...itemsToConvert.events.postop,
    ]
      .filter((action) => {
        return !deleteActions.includes(action.id!);
      })
      .map((action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = action;

        return {
          ...rest,
          reminders: processReminders(action.reminders),
        };
      });

    return { actions, broadcasts, events };
  },

  updateDeleteCarePlanItems: (deleteItems) =>
    set({ deleteCarePlanItems: deleteItems }),
  editEnrollmentItemsPayload: () => {
    const itemsToConvert = get().sortedCarePlanActions;
    const deleteActions = get().deletedActions;
    const currentCarePlan = get().currentCarePlan;

    if (!itemsToConvert) {
      return { actions: [], broadcasts: [], events: [] };
    }

    const processReminders = (reminders?: IActionReminder[]) => {
      if (
        !reminders ||
        (reminders.length === 1 && reminders[0].message === "")
      ) {
        return [];
      }
      // make sure reminder destination is set
      return reminders.map((reminder) => ({
        ...reminder,
        destination: currentCarePlan?.email,
        reminder_type: ReminderReminderTypeEnum.Email,
        reminder_repeat_interval_days: 0,
        always_send: false,
      }));
    };

    const actions = [
      ...itemsToConvert.actions.preop,
      ...itemsToConvert.actions.postop,
    ]
      .filter((action) => {
        return !(action.id && deleteActions.includes(action.id!));
      })
      .map((action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ...rest } = action;
        if (action.id && isMomentGeneratedId(action.id)) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...withoutId } = rest;
          return {
            ...withoutId,
            required: action.required ?? true,
            status: (action.status || "pending") as ActionStatusEnum,
            reminders: processReminders(action.reminders),
            source: action.source || "custom",
          };
        }
        return {
          ...rest,
          required: action.required ?? true,
          status: (action.status || "pending") as ActionStatusEnum,
          reminders: processReminders(action.reminders),
          source: action.source || "custom",
        };
      });

    const broadcasts = [
      ...itemsToConvert.exercises.preop,
      ...itemsToConvert.exercises.postop,
      ...itemsToConvert.education.preop,
      ...itemsToConvert.education.postop,
    ]
      .filter((action) => {
        return !(action.id && deleteActions.includes(action.id!));
      })
      .map((action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ...rest } = action;
        const validAssets = Array.isArray(action.assets)
          ? action.assets.filter((a) => !!a?.id)
          : [];
        const legacyAssetValid = !!action.asset?.id;
        const assetValue = legacyAssetValid
          ? {
              ...action.asset,
              name: action.asset?.name || action.title,
            }
          : null;
        if (action.id && isMomentGeneratedId(action.id)) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...withoutId } = rest;
          return {
            ...withoutId,
            category: action.category as BroadcastCategoryEnum,
            asset: assetValue,
            assets: validAssets,
            reminders: processReminders(action.reminders),
          } as unknown as Broadcast;
        }
        return {
          ...rest,
          category: (action.category || "Exercise") as BroadcastCategoryEnum,
          asset: assetValue,
          assets: validAssets,
          reminders: processReminders(action.reminders),
        } as unknown as Broadcast;
      });

    const events = [
      ...itemsToConvert.events.preop,
      ...itemsToConvert.events.postop,
    ]
      .filter((action) => {
        return !(action.id && deleteActions.includes(action.id!));
      })
      .map((action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ...rest } = action;
        if (action.id && isMomentGeneratedId(action.id)) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...withoutId } = rest;
          return {
            ...withoutId,
            reminders: processReminders(action.reminders),
          };
        }
        return {
          ...rest,
          reminders: processReminders(action.reminders),
        };
      });
    return { actions, broadcasts, events };
  },

  deleteEnrollmentItemsPayload: () => {
    const deleteItems = get().deleteCarePlanItems;
    const currentCarePlan = get().currentCarePlan;
    if (!currentCarePlan)
      return {
        actions: [],
        events: [],
        broadcasts: [],
      };

    const allCareplanItems = {
      actions: currentCarePlan.actions,
      events: currentCarePlan.events,
      broadcasts: currentCarePlan.broadcasts,
    };

    const actions = deleteItems.actions.filter((item) =>
      allCareplanItems.actions.some((i) => i.id === item)
    );

    const broadcasts = deleteItems.broadcasts.filter((item) =>
      allCareplanItems.broadcasts.some((i) => i.id === item)
    );

    const events = deleteItems.events.filter((item) =>
      allCareplanItems.events.some((i) => i.id === item)
    );

    return { actions, broadcasts, events };
  },
});
